import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sliders, MessageSquare, Palette } from 'lucide-react';

const tones = [
  { label: 'Professional', preview: 'Thank you for reaching out. I\'d be happy to assist you in finding the right plan for your needs. Could you share a bit more about what you\'re looking to accomplish?' },
  { label: 'Friendly',     preview: 'Hey! Thanks for reaching out — I\'d love to help you find the perfect plan. What are you looking to solve today?' },
  { label: 'Concise',      preview: 'Hi! Which plan are you interested in? I can help you decide quickly.' },
  { label: 'Empathetic',   preview: 'I completely understand how important this is for you. Let\'s find the best plan together — what matters most to your team?' },
  { label: 'Formal',       preview: 'Good day. I am here to assist you with your enquiry regarding our available plans. Please describe your requirements.' },
];

const tabs = [
  { id: 'tone',     icon: Sliders,      label: 'Tone' },
  { id: 'prompt',   icon: MessageSquare,label: 'Prompt' },
  // { id: 'workflow', icon: GitBranch,    label: 'Workflow' },
  { id: 'brand',    icon: Palette,      label: 'Brand' },
  // { id: 'confidence', icon: Gauge,      label: 'Confidence' },
];

function TonePanel() {
  const [selected, setSelected] = useState('Friendly');
  const preview = tones.find(t => t.label === selected)?.preview;
  return (
    <div>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Choose how your AI communicates with customers</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {tones.map(({ label }) => (
          <motion.button key={label} onClick={() => setSelected(label)}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '8px 18px', borderRadius: 999, fontSize: 14, fontWeight: 500, cursor: 'pointer',
              border: selected === label ? '1px solid #22D3EE' : '1px solid rgba(30,41,59,0.8)',
              background: selected === label ? 'linear-gradient(135deg,rgba(71,158,195,0.2),rgba(34,211,238,0.2))' : 'rgba(15,23,42,0.6)',
              color: selected === label ? '#22D3EE' : '#64748b',
              transition: 'all 0.2s',
            }}>{label}</motion.button>
        ))}
      </div>
      <motion.div key={selected} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
        style={{ marginTop: 20, padding: 16, borderRadius: 12, background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.15)', minHeight: 80 }}>
        <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
          Preview: <span style={{ color: '#a5f3fc' }}>"{preview}"</span>
        </p>
      </motion.div>
    </div>
  );
}

function PromptPanel() {
  return (
    <div>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Customize your AI's system prompt</p>
      <textarea
        defaultValue={`You are Pulse, a helpful AI assistant for Acme Corp. Always greet users warmly, prioritize resolving issues in under 3 turns, and escalate billing disputes to human agents.`}
        style={{
          width: '100%', height: 120, background: 'rgba(2,6,23,0.8)', border: '1px solid rgba(30,41,59,0.8)',
          borderRadius: 10, padding: 14, color: '#a5f3fc', fontSize: 13, lineHeight: 1.6,
          resize: 'none', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function WorkflowPanel() {
  const steps = ['User sends message', 'Check FAQ knowledge base', 'Generate response', 'Lead captured?', 'Notify sales team'];
  return (
    <div>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Visual workflow builder</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {steps.map((s, i) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg,#479EC3,#22D3EE)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: '#fff',
            }}>{i + 1}</div>
            <div style={{ flex: 1, padding: '8px 14px', borderRadius: 8, background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(30,41,59,0.6)', fontSize: 13, color: '#94a3b8' }}>{s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandPanel() {
  const colors = ['#479EC3', '#22D3EE', '#6366f1', '#10b981', '#f59e0b', '#ef4444'];
  const [active, setActive] = useState('#22D3EE');
  return (
    <div>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Match your brand identity</p>
      <p style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>Primary color</p>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {colors.map(c => (
          <button key={c} onClick={() => setActive(c)} style={{
            width: 32, height: 32, borderRadius: '50%', background: c, cursor: 'pointer',
            border: active === c ? '3px solid #fff' : '3px solid transparent',
            outline: 'none',
          }} />
        ))}
      </div>
      <div style={{ padding: 16, borderRadius: 12, background: 'rgba(15,23,42,0.6)', border: `1px solid ${active}44` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: active, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>P</div>
          <span style={{ fontSize: 14, color: '#f1f5f9', fontWeight: 600 }}>Pulse AI</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: active }}>● Online</span>
        </div>
      </div>
    </div>
  );
}

function ConfidencePanel() {
  const [val, setVal] = useState(78);
  return (
    <div>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Set minimum confidence threshold before AI responds</p>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, color: '#94a3b8' }}>Confidence threshold</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#22D3EE' }}>{val}%</span>
      </div>
      <input type="range" min={40} max={99} value={val} onChange={e => setVal(Number(e.target.value))}
        style={{ width: '100%', accentColor: '#22D3EE' }} />
      <p style={{ fontSize: 12, color: '#475569', marginTop: 12 }}>
        Below this threshold, Pulse will escalate to a human agent automatically.
      </p>
    </div>
  );
}

const panels = { tone: TonePanel, prompt: PromptPanel, workflow: WorkflowPanel, brand: BrandPanel, confidence: ConfidencePanel };

export default function Personalization() {
  const [active, setActive] = useState('tone');
  const Panel = panels[active];

  return (
    <section style={{ padding: '100px 24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', bottom: 0, right: 0, width: 400, height: 400, background: 'rgba(99,102,241,0.06)', borderRadius: '50%', filter: 'blur(100px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1000, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 56 }}>
          <p style={{ fontSize: 13, color: '#479EC3', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Personalization</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, lineHeight: 1.15, marginBottom: 14, letterSpacing: '-0.02em' }}>
            Make your AI sound exactly like your team.
          </h2>
          <p style={{ fontSize: 17, color: '#64748b', maxWidth: 480, margin: '0 auto' }}>
            Full control over tone, behavior, brand, and escalation — no engineering required.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
          style={{ background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(24px)', border: '1px solid rgba(30,41,59,0.6)', borderRadius: 16, overflow: 'hidden' }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(30,41,59,0.8)', overflowX: 'auto' }}>
            {tabs.map(t => {
              const Icon = t.icon;
              const isActive = active === t.id;
              return (
                <button key={t.id} onClick={() => setActive(t.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '14px 22px',
                    background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                    borderBottom: isActive ? '2px solid #22D3EE' : '2px solid transparent',
                    color: isActive ? '#22D3EE' : '#64748b',
                    fontSize: 14, fontWeight: isActive ? 600 : 400,
                    transition: 'color 0.2s',
                  }}>
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </div>
          {/* Panel */}
          <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
            style={{ padding: 32, minHeight: 280 }}>
            <Panel />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
